/**
 * Item Tooltip - Renders detailed item information on hover
 * Rich visual tooltip with tier indicators, DPS calculation,
 * gradient backgrounds, comparison arrows, and enhancement sparkles.
 */

import { EquipmentItem, ItemMod, DefenseType, WeaponSubType } from '@shared/types';
import { GameItem, getItemColor, CurrencyItem, QuestItem } from '@shared/items/Item';
import { LegendaryItem } from '@shared/items/LegendaryItems';
import { compareItems, renderItemComparisonPanel } from './ItemComparison';
import { getSetById } from '@shared/items/SetItemSystem';
import { t } from '@locales/index';
import { getTierForValue } from '@shared/items/ModifierPool';

// Slot display name via locale
function getSlotName(slot: string): string {
  return t(`items.slots.${slot}`) || slot;
}

// Defense type display name via locale
function getDefenseTypeName(dt: DefenseType): string {
  const map: Record<DefenseType, string> = {
    'armour': t('inventory.defenseArmour'),
    'evasion': t('inventory.defenseEvasion'),
    'energyShield': t('inventory.defenseEnergyShield'),
    'armour_evasion': t('inventory.defenseArmourEvasion'),
    'armour_energyShield': t('inventory.defenseArmourES'),
    'evasion_energyShield': t('inventory.defenseEvasionES'),
  };
  return map[dt] || dt;
}

// Weapon sub-type display name via locale
function getWeaponSubTypeName(wst: WeaponSubType): string {
  const map: Record<WeaponSubType, string> = {
    'sword': t('inventory.weaponSword'),
    'axe': t('inventory.weaponAxe'),
    'bow': t('inventory.weaponBow'),
    'staff': t('inventory.weaponStaff'),
    'dagger': t('inventory.weaponDagger'),
    'wand': t('inventory.weaponWand'),
    'great_sword': t('inventory.weaponGreatSword'),
    'great_axe': t('inventory.weaponGreatAxe'),
    'hammer': t('inventory.weaponHammer'),
  };
  return map[wst] || wst;
}

// Mod display name via locale
function getModName(stat: string): string {
  const localized = t(`items.mods.${stat}`);
  // If locale key not found, t() returns the key path itself
  return localized.startsWith('items.mods.') ? stat : localized;
}

/** A text segment within a tooltip line (supports multi-color text per line) */
interface TextSegment {
  text: string;
  color: string;
  bold?: boolean;
  size?: number;
}

/** A single tooltip line that can contain multiple colored segments */
interface TooltipLine {
  segments: TextSegment[];
  /** Line type for special rendering */
  type?: 'normal' | 'separator' | 'rarityBand' | 'dps';
  /** Whether this line should have an alternating stripe background */
  stripe?: boolean;
}

/** Get tier color: T1=gold, T2=silver, T3=bronze, T4=teal, T5-T8=gray shades */
function getTierColor(tier: number): string {
  switch (tier) {
    case 1: return '#ffd700';
    case 2: return '#c0c0c0';
    case 3: return '#cd7f32';
    case 4: return '#5faaaa';
    case 5: return '#8888aa';
    case 6: return '#777788';
    case 7: return '#666677';
    default: return '#555566';
  }
}

/**
 * Get the value range for a mod using actual tier data from ModifierPool.
 * Falls back to percentage-based estimation for legacy mods.
 */
function getModRange(mod: ItemMod): { min: number; max: number } {
  // Try to get actual tier data from ModifierPool
  const tierEntry = getTierForValue(mod.type, mod.calcType, mod.value);
  if (tierEntry) {
    return { min: tierEntry.valueMin, max: tierEntry.valueMax };
  }

  // Fallback: percentage-based estimation for legacy/unknown mods
  const tier = mod.tier;
  let lowPct: number;
  let highPct: number;
  switch (tier) {
    case 1: lowPct = 0.80; highPct = 1.00; break;
    case 2: lowPct = 0.60; highPct = 0.80; break;
    case 3: lowPct = 0.40; highPct = 0.60; break;
    default: lowPct = 0.20; highPct = 0.40; break;
  }
  const absVal = Math.abs(mod.value);
  const min = Math.floor(absVal * lowPct);
  const max = Math.ceil(absVal * highPct);
  return {
    min: Math.min(min, Math.abs(mod.value)),
    max: Math.max(max, Math.abs(mod.value)),
  };
}

/** Parse a hex color and return rgba with specified alpha */
function hexToRgba(hex: string, alpha: number): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

export class ItemTooltip {
  private visible: boolean = false;
  private item: GameItem | null = null;
  private x: number = 0;
  private y: number = 0;
  private compareItem: EquipmentItem | null = null;
  private equippedSetIds: string[] = [];

  /** Set currently equipped set IDs for set bonus display */
  setEquippedSetIds(setIds: string[]): void {
    this.equippedSetIds = setIds;
  }

  show(item: GameItem, x: number, y: number, compareItem?: EquipmentItem | null): void {
    this.item = item;
    this.x = x;
    this.y = y;
    this.visible = true;
    this.compareItem = compareItem || null;
  }

  hide(): void {
    this.visible = false;
    this.item = null;
    this.compareItem = null;
  }

  isVisible(): boolean {
    return this.visible;
  }

  render(ctx: CanvasRenderingContext2D, screenWidth: number, screenHeight: number): void {
    if (!this.visible || !this.item) return;

    const item = this.item;

    // Calculate tooltip dimensions
    const padding = 12;
    const lineHeight = 20;
    const lines = this.buildTooltipLines(item);

    const maxWidth = Math.max(...lines.map(l => this.measureLineWidth(ctx, l)));
    const width = maxWidth + padding * 2 + 24;
    const height = lines.length * lineHeight + padding * 2 + 8;

    // --- Smooth Positioning (Section 6) ---
    const margin = 5;

    // Preferred: right of cursor, above cursor
    let tx = this.x + 15;
    let ty = this.y - height - 10;

    // If tooltip goes off the right edge, flip to left of cursor
    if (tx + width > screenWidth - margin) {
      tx = this.x - width - 15;
    }
    // If tooltip goes off the left edge, clamp to margin
    if (tx < margin) {
      tx = margin;
    }

    // If tooltip goes off the top edge, flip to below cursor
    if (ty < margin) {
      ty = this.y + 20;
    }
    // If tooltip goes off the bottom edge, clamp
    if (ty + height > screenHeight - margin) {
      ty = screenHeight - height - margin;
    }

    const rarityColor = getItemColor(item.rarity);

    ctx.save();

    // --- Gradient Background (Section 4) ---
    const bgGradient = ctx.createLinearGradient(tx, ty, tx, ty + height);
    bgGradient.addColorStop(0, 'rgba(25, 20, 15, 0.97)');
    bgGradient.addColorStop(1, 'rgba(15, 12, 8, 0.97)');
    ctx.fillStyle = bgGradient;
    ctx.fillRect(tx, ty, width, height);

    // --- Border with rarity color ---
    ctx.strokeStyle = rarityColor;
    ctx.lineWidth = 2;
    ctx.strokeRect(tx, ty, width, height);

    // --- Inner glow line (Section 4): 1px inside border, rarity color at 30% opacity ---
    ctx.strokeStyle = hexToRgba(rarityColor, 0.3);
    ctx.lineWidth = 1;
    ctx.strokeRect(tx + 2, ty + 2, width - 4, height - 4);

    // --- Render lines ---
    let curY = ty + padding;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'top';

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const lineTop = curY;

      // --- Rarity band background (Section 4) ---
      if (line.type === 'rarityBand') {
        ctx.fillStyle = hexToRgba(rarityColor, 0.10);
        ctx.fillRect(tx + 2, lineTop, width - 4, lineHeight);
      }

      // --- Alternating stripe backgrounds for mod lines (Section 4) ---
      if (line.stripe) {
        ctx.fillStyle = 'rgba(255, 255, 255, 0.03)';
        ctx.fillRect(tx + 2, lineTop, width - 4, lineHeight);
      }

      // --- Separator lines: gradient from transparent to rarity color to transparent (Section 4) ---
      if (line.type === 'separator') {
        const sepY = lineTop + lineHeight / 2;
        const sepGrad = ctx.createLinearGradient(tx + padding, sepY, tx + width - padding, sepY);
        sepGrad.addColorStop(0, 'rgba(0, 0, 0, 0)');
        sepGrad.addColorStop(0.3, hexToRgba(rarityColor, 0.5));
        sepGrad.addColorStop(0.5, hexToRgba(rarityColor, 0.7));
        sepGrad.addColorStop(0.7, hexToRgba(rarityColor, 0.5));
        sepGrad.addColorStop(1, 'rgba(0, 0, 0, 0)');
        ctx.strokeStyle = sepGrad;
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(tx + padding, sepY);
        ctx.lineTo(tx + width - padding, sepY);
        ctx.stroke();
        curY += lineHeight;
        continue;
      }

      // --- Render text segments ---
      let segX = tx + padding;
      for (const seg of line.segments) {
        const fontSize = seg.size || 13;
        ctx.font = `${seg.bold ? 'bold ' : ''}${fontSize}px Arial`;
        ctx.fillStyle = seg.color;
        ctx.fillText(seg.text, segX, lineTop + (lineHeight - fontSize) / 2);
        segX += ctx.measureText(seg.text).width;
      }

      curY += lineHeight;
    }

    ctx.restore();

    // --- Render compact comparison panel alongside the tooltip ---
    if (this.compareItem && 'slot' in item) {
      const equip = item as EquipmentItem;
      if (this.compareItem.slot === equip.slot) {
        const comparisons = compareItems(equip, this.compareItem);
        if (comparisons.length > 0) {
          const compWidth = 160;
          const compMargin = 4;

          // Try to place comparison panel to the right of the tooltip
          let compX = tx + width + compMargin;
          if (compX + compWidth > screenWidth - margin) {
            // If no space on the right, try the left side
            compX = tx - compWidth - compMargin;
          }
          if (compX < margin) {
            compX = margin;
          }

          renderItemComparisonPanel(ctx, comparisons, compX, ty, compWidth);
        }
      }
    }
  }

  private buildTooltipLines(item: GameItem): TooltipLine[] {
    const lines: TooltipLine[] = [];
    const rarityColor = getItemColor(item.rarity);

    // --- Item name with diamond decoration (Section 4) ---
    lines.push({
      segments: [
        { text: '\u25C6 ', color: rarityColor, bold: true, size: 14 },
        { text: item.name, color: rarityColor, bold: true, size: 14 },
      ],
    });

    if ('slot' in item) {
      const equip = item as EquipmentItem;
      const isEquipment = true;

      // --- Rarity + Slot with rarity band (Section 4) ---
      const rarityName = this.getRarityName(equip.rarity);
      const slotName = getSlotName(equip.slot);
      lines.push({
        segments: [{ text: `${rarityName} ${slotName}`, color: '#888888' }],
        type: 'rarityBand',
      });

      // Defense type (AR/EV/ES) for armour pieces
      if (equip.defenseType) {
        lines.push({
          segments: [{ text: getDefenseTypeName(equip.defenseType), color: '#888888' }],
        });
      }

      // Weapon sub-type for weapons
      if (equip.weaponSubType) {
        lines.push({
          segments: [{ text: getWeaponSubTypeName(equip.weaponSubType), color: '#888888' }],
        });
      }

      // Item level + ilvl
      const ilvlText = equip.ilvl ? ` (iLvl: ${equip.ilvl})` : '';
      lines.push({
        segments: [{ text: `Seviye: ${equip.level}${ilvlText}`, color: '#888888' }],
      });

      // --- Enhancement display (Section 7) ---
      if (equip.enhancement && equip.enhancement > 0) {
        const sparkleCount = Math.floor(equip.enhancement / 5);
        const sparkles = sparkleCount > 0 ? ' ' + '\u2726'.repeat(sparkleCount) : '';
        lines.push({
          segments: [
            { text: `+${equip.enhancement} Guclendirme`, color: '#ffdd44', bold: true },
            { text: sparkles, color: '#ffdd44' },
          ],
        });
      }

      // Separator
      lines.push({ segments: [], type: 'separator' });

      // Implicit mods
      if (equip.implicit && equip.implicit.length > 0) {
        let stripeToggle = false;
        for (const mod of equip.implicit) {
          lines.push({
            segments: this.buildModSegments(mod, isEquipment),
            stripe: stripeToggle,
          });
          stripeToggle = !stripeToggle;
        }
        lines.push({ segments: [], type: 'separator' });
      }

      // --- DPS Calculation for weapons (Section 3) ---
      if (equip.slot === 'weapon') {
        const dps = this.calculateDPS(equip);
        lines.push({
          segments: [{ text: `DPS: ${dps.toFixed(1)}`, color: '#ffd700', bold: true, size: 14 }],
          type: 'dps',
        });
        lines.push({ segments: [], type: 'separator' });
      }

      // Prefix mods
      let modStripeToggle = false;
      if (equip.prefixes && equip.prefixes.length > 0) {
        for (const mod of equip.prefixes) {
          lines.push({
            segments: this.buildModSegments(mod, isEquipment),
            stripe: modStripeToggle,
          });
          modStripeToggle = !modStripeToggle;
        }
      }

      // Suffix mods
      if (equip.suffixes && equip.suffixes.length > 0) {
        for (const mod of equip.suffixes) {
          lines.push({
            segments: this.buildModSegments(mod, isEquipment),
            stripe: modStripeToggle,
          });
          modStripeToggle = !modStripeToggle;
        }
      }

      // --- Legendary Effects Display ---
      const legendary = equip as unknown as LegendaryItem;
      if (legendary.legendaryEffects && legendary.legendaryEffects.length > 0) {
        lines.push({ segments: [], type: 'separator' });

        for (const effect of legendary.legendaryEffects) {
          // Effect description in gold/orange
          lines.push({
            segments: [{ text: `\u2726 ${effect.description}`, color: '#ffa500', bold: true }],
          });

          // Proc chance, cooldown, duration details in dimmer color
          const details: string[] = [];
          if (effect.chance !== undefined) {
            details.push(`Sans: %${effect.chance}`);
          }
          if (effect.cooldown !== undefined) {
            details.push(`Bekleme: ${effect.cooldown}sn`);
          }
          if (effect.duration !== undefined) {
            details.push(`Sure: ${effect.duration}sn`);
          }
          if (details.length > 0) {
            lines.push({
              segments: [{ text: `   ${details.join(' | ')}`, color: '#888866', size: 11 }],
            });
          }
        }
      }

      // --- Fixed Mods (Legendary) ---
      if (legendary.fixedMods && legendary.fixedMods.length > 0) {
        lines.push({ segments: [], type: 'separator' });
        lines.push({
          segments: [{ text: 'Sabit Modlar:', color: '#ffd700', bold: true }],
        });
        let fixedModStripe = false;
        for (const mod of legendary.fixedMods) {
          lines.push({
            segments: this.buildModSegments(mod, isEquipment),
            stripe: fixedModStripe,
          });
          fixedModStripe = !fixedModStripe;
        }
      }

      // --- Flavor Text (Legendary) ---
      if (legendary.flavorText) {
        lines.push({ segments: [], type: 'separator' });
        lines.push({
          segments: [{ text: `"${legendary.flavorText}"`, color: '#999999', size: 11 }],
        });
      }

      // --- Class Restriction ---
      if (legendary.classRestriction && legendary.classRestriction.length > 0) {
        const classNames = legendary.classRestriction.map((c: string) => {
          switch (c) {
            case 'alp': return 'Alp';
            case 'kam': return 'Kam';
            case 'mergen': return 'Mergen';
            default: return c;
          }
        });
        lines.push({
          segments: [{ text: `Sinif: ${classNames.join(', ')}`, color: '#ff6666' }],
        });
      }

      // Set Bonus Preview
      if (equip.setId) {
        const setInfo = getSetById(equip.setId);
        if (setInfo) {
          const equippedCount = this.equippedSetIds.filter(id => id === equip.setId).length;
          lines.push({ segments: [{ text: '', color: '#333333' }] });
          lines.push({
            segments: [
              { text: `${setInfo.name}`, color: '#00cc00', bold: true },
              { text: ` (${equippedCount}/${setInfo.pieces.length})`, color: '#808080' },
            ],
          });
          for (const bonus of setInfo.bonuses) {
            const isActive = equippedCount >= bonus.piecesRequired;
            const prefix = isActive ? '\u2714 ' : '\u2022 ';
            const color = isActive ? '#00cc00' : '#555555';
            const modTexts = bonus.modifiers.map(m => {
              const sign = m.value >= 0 ? '+' : '';
              const suffix = m.isPercent ? '%' : '';
              const statName = getModName(m.stat);
              return `${sign}${m.value}${suffix} ${statName}`;
            });
            let bonusText = `${prefix}(${bonus.piecesRequired}) ${modTexts.join(', ')}`;
            if (bonus.specialEffect) {
              bonusText += modTexts.length > 0 ? `, ${bonus.specialEffect}` : `${prefix}(${bonus.piecesRequired}) ${bonus.specialEffect}`;
              if (modTexts.length === 0) bonusText = `${prefix}(${bonus.piecesRequired}) ${bonus.specialEffect}`;
            }
            lines.push({ segments: [{ text: bonusText, color }] });
          }
        }
      }

      // --- Comparison with equipped item (Section 5) ---
      if (this.compareItem && this.compareItem.slot === equip.slot) {
        lines.push({ segments: [{ text: '', color: '#333333' }] });
        const compareRarityColor = getItemColor(this.compareItem.rarity);
        lines.push({
          segments: [
            { text: '\u2500\u2500 ', color: '#aaaaaa' },
            { text: 'Kusanilmis', color: compareRarityColor, bold: true },
            { text: ': ', color: '#aaaaaa' },
            { text: this.compareItem.name, color: compareRarityColor },
            { text: ' \u2500\u2500', color: '#aaaaaa' },
          ],
        });
        this.addComparisonLines(lines, equip, this.compareItem);
      }

    } else if ('currencyType' in item) {
      const currency = item as CurrencyItem;
      lines.push({
        segments: [{ text: `Miktar: ${currency.stackSize}`, color: '#ffd700' }],
      });

    } else if ('questId' in item) {
      const quest = item as unknown as QuestItem;
      lines.push({
        segments: [{ text: t('items.questItem'), color: '#ff8844', bold: true }],
      });
      if (quest.description) {
        // Word wrap description
        const words = quest.description.split(' ');
        let currentLine = '';
        for (const word of words) {
          if (currentLine.length + word.length > 35) {
            lines.push({ segments: [{ text: currentLine, color: '#aaaaaa' }] });
            currentLine = word;
          } else {
            currentLine += (currentLine ? ' ' : '') + word;
          }
        }
        if (currentLine) {
          lines.push({ segments: [{ text: currentLine, color: '#aaaaaa' }] });
        }
      }
    }

    return lines;
  }

  /**
   * Build colored segments for a single mod line.
   * Includes tier tag, mod value+name, and optional value range.
   */
  private buildModSegments(mod: ItemMod, showRange: boolean): TextSegment[] {
    const segments: TextSegment[] = [];
    const name = getModName(mod.type);
    const isPercent = mod.calcType === 'increased' || mod.calcType === 'more' ||
                      mod.calcType === 'reduced' || mod.calcType === 'less';
    const sign = mod.value >= 0 ? '+' : '';
    const tierColor = getTierColor(mod.tier);

    // Tier tag: [T1]
    segments.push({ text: `[T${mod.tier}] `, color: tierColor, bold: true });

    // Mod value and name
    const modColor = '#4488ff';
    segments.push({ text: `${sign}${mod.value}${isPercent ? '%' : ''} ${name}`, color: modColor });

    // Value range for equipment items (uses actual tier data from ModifierPool)
    if (showRange) {
      const range = getModRange(mod);
      segments.push({ text: ` (${range.min}-${range.max})`, color: '#666666' });
    }

    return segments;
  }

  /**
   * Calculate DPS for weapon items.
   * Formula: baseDamage * (1 + attackSpeed / 100)
   * If no explicit damage mod, estimate from level: level * 5
   */
  private calculateDPS(equip: EquipmentItem): number {
    const allMods = [
      ...(equip.implicit || []),
      ...(equip.prefixes || []),
      ...(equip.suffixes || []),
    ];

    let baseDamage = 0;
    let attackSpeed = 0;

    for (const mod of allMods) {
      if (mod.type === 'damage') {
        baseDamage += mod.value;
      } else if (mod.type === 'attackSpeed') {
        attackSpeed += mod.value;
      }
    }

    // If no explicit damage mod, estimate from level
    if (baseDamage === 0) {
      baseDamage = equip.level * 5;
    }

    return baseDamage * (1 + attackSpeed / 100);
  }

  /**
   * Add comparison lines with arrows and percentage improvements (Section 5).
   */
  private addComparisonLines(
    lines: TooltipLine[],
    newItem: EquipmentItem,
    oldItem: EquipmentItem
  ): void {
    const newMods = this.aggregateMods(newItem);
    const oldMods = this.aggregateMods(oldItem);

    const allKeys = new Set([...Object.keys(newMods), ...Object.keys(oldMods)]);

    for (const key of allKeys) {
      const newVal = newMods[key] || 0;
      const oldVal = oldMods[key] || 0;
      const diff = newVal - oldVal;

      if (diff === 0) continue;

      const name = getModName(key);
      const isIncrease = diff > 0;
      const arrow = isIncrease ? '\u25B2' : '\u25BC';
      const arrowColor = isIncrease ? '#44ff44' : '#ff4444';
      const sign = diff > 0 ? '+' : '';

      const segments: TextSegment[] = [];
      segments.push({ text: `${arrow} `, color: arrowColor, bold: true });
      segments.push({ text: `${sign}${diff} ${name}`, color: arrowColor });

      // Show percentage improvement if old value is non-zero
      if (oldVal !== 0) {
        const pctChange = Math.abs(Math.round((diff / Math.abs(oldVal)) * 100));
        const pctArrow = isIncrease ? '\u25B2' : '\u25BC';
        segments.push({ text: ` (${pctArrow} ${pctChange}%)`, color: '#aaaaaa' });
      }

      lines.push({ segments });
    }
  }

  private aggregateMods(item: EquipmentItem): Record<string, number> {
    const mods: Record<string, number> = {};
    const allMods = [
      ...(item.implicit || []),
      ...(item.prefixes || []),
      ...(item.suffixes || []),
    ];
    for (const mod of allMods) {
      mods[mod.type] = (mods[mod.type] || 0) + mod.value;
    }
    return mods;
  }

  private getRarityName(rarity: string): string {
    switch (rarity) {
      case 'normal': return 'Normal';
      case 'magic': return 'Buyulu';
      case 'rare': return 'Nadir';
      case 'set': return 'Set';
      case 'legendary': return 'Efsanevi';
      default: return rarity;
    }
  }

  /** Measure total width of all segments in a tooltip line */
  private measureLineWidth(ctx: CanvasRenderingContext2D, line: TooltipLine): number {
    if (line.type === 'separator') return 100; // separators have fixed visual width
    let total = 0;
    for (const seg of line.segments) {
      const fontSize = seg.size || 13;
      ctx.font = `${seg.bold ? 'bold ' : ''}${fontSize}px Arial`;
      total += ctx.measureText(seg.text).width;
    }
    return total;
  }
}
